/**
 * Ordering for the Browse / Swap meal list.
 *
 * Always operates on a CLONE — the React `meals` state array is never sorted
 * in place.
 *
 * SERVER-RANKED (Phase 2B backend): every card carries `server_rank`, the
 * backend's AUTHORITATIVE order (nutrition group — every acceptable card
 * before any Out of Range one — then the soft-filter match, the preference
 * tier, a same-day repeat, then goal-aware projected-day quality). The list is
 * returned in exactly that order: allergen / dislike / cookware flags, legacy
 * `fits`, rank_tier and favorites never re-rank it.
 *
 * LEGACY (a response without server_rank):
 *   SWAP MODE: the backend's projected-day nutrition tier (rank_tier 0 > 1 > 2)
 *   is authoritative and must come BEFORE any favorite preference. Favorites
 *   may only reorder meals WITHIN the same nutrition/safety tier. The backend's
 *   safety/preference ordering (allergen, dislike, missing cookware, cook time)
 *   is preserved, and the backend's within-tier order (priority_score) breaks
 *   remaining ties.
 *   REGULAR BROWSE: compatible-first, then favorites, then priority_score.
 */

/** True when every card carries the backend's authoritative server_rank. */
export function isServerRanked(meals) {
  var list = meals || [];
  return list.length > 0 && list.every(function (m) {
    return m && typeof m.server_rank === 'number';
  });
}

function rankTierKey(m) {
  var rt = m ? m.rank_tier : null;
  // null (targets not ready) -> neutral bucket so these never reorder among
  // themselves; priority_score then preserves the backend order.
  return (rt == null) ? 99 : rt;
}

export function sortBrowseMeals(meals, opts) {
  opts = opts || {};
  var isSwapMode = !!opts.isSwapMode;
  var activeCategory = opts.activeCategory;
  var favorites = opts.favorites || {};

  var arr = (meals || []).slice(); // clone — never mutate the input array

  if (isServerRanked(arr)) {
    arr.sort(function (a, b) { return a.server_rank - b.server_rank; });
    return arr;
  }

  arr.sort(function (a, b) {
    // Shared safety ordering (preserved from the backend contract).
    var aAll = a.has_allergen ? 1 : 0, bAll = b.has_allergen ? 1 : 0;
    if (aAll !== bAll) return aAll - bAll;

    var aDis = a.has_dislike ? 1 : 0, bDis = b.has_dislike ? 1 : 0;
    if (aDis !== bDis) return aDis - bDis;

    var aMiss = (a.missing_cookware && a.missing_cookware.length > 0) ? 1 : 0;
    var bMiss = (b.missing_cookware && b.missing_cookware.length > 0) ? 1 : 0;
    if (aMiss !== bMiss) return aMiss - bMiss;

    if (isSwapMode) {
      // Nutrition tier is authoritative BEFORE favorites.
      var aT = rankTierKey(a), bT = rankTierKey(b);
      if (aT !== bT) return aT - bT;

      // Cook-time preference is part of the backend safety/preference order.
      var aCk = a.exceeds_cook_time ? 1 : 0, bCk = b.exceeds_cook_time ? 1 : 0;
      if (aCk !== bCk) return aCk - bCk;

      // Favorites only reorder WITHIN the same tier.
      if (activeCategory !== 'Favorites') {
        var aF = favorites[a.meal_id] ? 1 : 0, bF = favorites[b.meal_id] ? 1 : 0;
        if (aF !== bF) return bF - aF;
      }

      return (b.priority_score || 0) - (a.priority_score || 0);
    }

    // Regular Browse (legacy, unchanged).
    var aFits = a.fits ? 1 : 0, bFits = b.fits ? 1 : 0;
    if (aFits !== bFits) return bFits - aFits;

    if (activeCategory !== 'Favorites') {
      var aFav = favorites[a.meal_id] ? 1 : 0, bFav = favorites[b.meal_id] ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
    }

    return (b.priority_score || 0) - (a.priority_score || 0);
  });

  return arr;
}
